import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { UserCircle, ChevronDown, Check, Plus, X, User } from 'lucide-react';
import { getBackendUrl } from '../utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const storageKey = (restaurantId) => `pedeefacil:${restaurantId || 'default'}:default_waiter`;

export const getDefaultWaiter = (restaurantId) => {
  try {
    const raw = localStorage.getItem(storageKey(restaurantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.name) return parsed;
    return null;
  } catch {
    return null;
  }
};

export const setDefaultWaiter = (restaurantId, waiter) => {
  try {
    if (!waiter) {
      localStorage.removeItem(storageKey(restaurantId));
      return;
    }
    localStorage.setItem(
      storageKey(restaurantId),
      JSON.stringify({
        id: waiter.id || null,
        name: waiter.name,
        savedAt: new Date().toISOString(),
      })
    );
  } catch {}
};

export const clearDefaultWaiter = (restaurantId) => setDefaultWaiter(restaurantId, null);

const WaiterSelector = ({
  restaurantId,
  value,
  onChange,
  onCreateTeamMember,
  autoloadDeviceDefault = true,
}) => {
  const [waiters, setWaiters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);

  const fetchWaiters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/team/waiters`);
      setWaiters(res.data?.waiters || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Não foi possível carregar a equipe.');
      setWaiters([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchWaiters(); }, [fetchWaiters]);

  useEffect(() => {
    if (!autoloadDeviceDefault) return;
    if (value && value.name) return;
    const saved = getDefaultWaiter(restaurantId);
    if (saved) onChange?.(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const handleSelect = (waiter) => {
    setDefaultWaiter(restaurantId, waiter);
    onChange?.(waiter);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    clearDefaultWaiter(restaurantId);
    onChange?.(null);
  };

  const initial = (value?.name || '?').trim().charAt(0).toUpperCase();

  return (
    <div className="relative" data-testid="waiter-selector">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-left hover:border-orange-500/60 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all"
        data-testid="waiter-selector-trigger"
      >
        {value && value.name ? (
          <>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {initial}
            </div>
            <span className="flex-1 text-white font-medium truncate">{value.name}</span>
            <span
              role="button" tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => (e.key === 'Enter' ? handleClear(e) : null)}
              className="p-1 rounded-full hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
              data-testid="waiter-selector-clear"
              title="Limpar seleção"
            >
              <X className="w-4 h-4" />
            </span>
          </>
        ) : (
          <>
            <UserCircle className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <span className="flex-1 text-gray-500">Selecionar atendente...</span>
          </>
        )}
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-2 w-full bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2" data-testid="waiter-selector-menu">
            {loading && <div className="px-4 py-3 text-gray-400 text-sm">Carregando equipe...</div>}
            {!loading && error && <div className="px-4 py-3 text-red-400 text-sm">{error}</div>}
            {!loading && !error && waiters.length === 0 && (
              <div className="px-4 py-4">
                <p className="text-gray-400 text-sm mb-3">Nenhum atendente cadastrado ainda. Cadastre garçons e caixas em Equipe.</p>
                {onCreateTeamMember && (
                  <button type="button" onClick={() => { setOpen(false); onCreateTeamMember(); }}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium text-sm transition-colors"
                    data-testid="waiter-selector-create">
                    <Plus className="w-4 h-4" /> Cadastrar atendente
                  </button>
                )}
              </div>
            )}
            {!loading && !error && waiters.length > 0 && (
              <ul className="max-h-64 overflow-y-auto py-1" data-testid="waiter-selector-list">
                {waiters.map((w) => {
                  const active = value?.id === w.id || (!value?.id && value?.name === w.name);
                  return (
                    <li key={w.id}>
                      <button type="button" onClick={() => handleSelect(w)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${active ? 'bg-orange-500/10' : 'hover:bg-gray-800'}`}
                        data-testid={`waiter-option-${w.id}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${active ? 'bg-gradient-to-br from-orange-500 to-pink-500' : 'bg-gray-700'}`}>
                          {(w.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-medium truncate">{w.name}</div>
                          <div className="text-gray-500 text-xs">{w.role === 'caixa' ? 'Caixa' : 'Garçom'}</div>
                        </div>
                        {active && <Check className="w-4 h-4 text-orange-400" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
      {value?.name && (
        <p className="mt-2 text-xs text-gray-500 flex items-center gap-1.5">
          <User className="w-3 h-3" />
          Este dispositivo está atendendo como <span className="text-orange-300 font-medium">{value.name}</span>
        </p>
      )}
    </div>
  );
};

export default WaiterSelector;
